import React, { useState, useEffect } from 'react';
import { listPullRequests, createPullRequest, mergePR, closePR, getPRComments, addPRComment, listBranches } from 'service/ncoreBoardService';
import { useAuth } from 'contexts/AuthContext';
import styles from './PullRequestsTab.module.scss';

function timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const d = Math.floor(diff / 86400000);
    if (d === 0) return 'сегодня';
    if (d === 1) return 'вчера';
    if (d < 30) return `${d}д. назад`;
    return new Date(dateStr).toLocaleDateString('ru-RU');
}

function Avatar({ user, size = 28 }) {
    return user?.avatar
        ? <img src={user.avatar} alt={user.username} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />
        : <div className={styles.avatarFallback} style={{ width: size, height: size, fontSize: size * 0.4 }}>
            {(user?.username || '?')[0].toUpperCase()}
          </div>;
}

function PRDetail({ repoId, pr, onBack, onMerge, onClose, isOwner }) {
    const { user } = useAuth();
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getPRComments(repoId, pr.id).then(data => {
            setComments(data.results || data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [repoId, pr.id]);

    async function submitComment(e) {
        e.preventDefault();
        if (!newComment.trim()) return;
        try {
            const comment = await addPRComment(repoId, pr.id, { body: newComment });
            setComments(prev => [...prev, comment]);
            setNewComment('');
        } catch (err) { alert(err.message); }
    }

    const stateColor = pr.state === 'open' ? '#4ade80' : pr.state === 'merged' ? '#a78bfa' : '#9ca3af';
    const stateLabel = pr.state === 'open' ? '● Открыт' : pr.state === 'merged' ? '⟵ Смержен' : '✗ Закрыт';

    return (
        <div className={styles.detail}>
            <button className={styles.backBtn} onClick={onBack}>← Назад к PR</button>
            <div className={styles.detailHeader}>
                <div className={styles.prTitleRow}>
                    <span className={styles.stateTag} style={{ color: stateColor, background: stateColor + '18', borderColor: stateColor + '44' }}>
                        {stateLabel}
                    </span>
                    <h2 className={styles.prTitle}>{pr.title} <span className={styles.prNumber}>#{pr.number}</span></h2>
                    {pr.is_draft && <span className={styles.draftBadge}>Черновик</span>}
                </div>
                <div className={styles.prBranch}>
                    <span className={styles.branchTag}>{pr.head_branch}</span>
                    <span className={styles.branchArrow}>→</span>
                    <span className={styles.branchTag}>{pr.base_branch}</span>
                </div>
                <div className={styles.prMeta}>
                    <Avatar user={pr.author} size={22} />
                    <span><strong>{pr.author?.username}</strong> создал {timeAgo(pr.created_at)}</span>
                    {pr.state !== 'open' && pr.additions !== undefined && (
                        <>
                            <span className={styles.additions}>+{pr.additions}</span>
                            <span className={styles.deletions}>-{pr.deletions}</span>
                        </>
                    )}
                </div>
                {pr.body && <p className={styles.prBody}>{pr.body}</p>}
            </div>

            {/* Action buttons for owner */}
            {isOwner && pr.state === 'open' && !pr.is_draft && (
                <div className={styles.prActions}>
                    <button className={styles.mergeBtn} onClick={() => onMerge(pr.id)}>
                        ⟵ Смержить PR
                    </button>
                    <button className={styles.closeBtn} onClick={() => onClose(pr.id)}>
                        ✗ Закрыть PR
                    </button>
                </div>
            )}

            {/* Comments */}
            {loading
                ? <div className={styles.loading}>Загрузка...</div>
                : comments.map(c => (
                    <div key={c.id} className={styles.commentCard}>
                        <div className={styles.commentHeader}>
                            <Avatar user={c.author} />
                            <strong>{c.author?.username}</strong>
                            {c.file_path && <span className={styles.fileRef}>{c.file_path}:{c.line_number}</span>}
                            <span className={styles.commentDate}>{timeAgo(c.created_at)}</span>
                        </div>
                        <div className={styles.commentBody}>{c.body}</div>
                    </div>
                ))
            }

            {user && (
                <form className={styles.commentForm} onSubmit={submitComment}>
                    <Avatar user={user} />
                    <div className={styles.commentInput}>
                        <textarea
                            placeholder="Оставить комментарий..."
                            value={newComment}
                            onChange={e => setNewComment(e.target.value)}
                            rows={3}
                        />
                        <div className={styles.commentActions}>
                            <button type="submit" className={styles.submitBtn} disabled={!newComment.trim()}>
                                Отправить
                            </button>
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
}

export default function PullRequestsTab({ repo, isOwner }) {
    const { user } = useAuth();
    const [prs, setPrs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stateFilter, setStateFilter] = useState('open');
    const [selectedPR, setSelectedPR] = useState(null);
    const [creating, setCreating] = useState(false);
    const [branches, setBranches] = useState([]);
    const [form, setForm] = useState({ title: '', body: '', head_branch: '', base_branch: repo.default_branch || 'main', is_draft: false });

    useEffect(() => { loadPRs(); }, [repo.id, stateFilter]);
    useEffect(() => {
        if (creating) {
            listBranches(repo.id).then(data => setBranches(data.results || data)).catch(() => {});
        }
    }, [creating, repo.id]);

    async function loadPRs() {
        setLoading(true);
        try {
            const data = await listPullRequests(repo.id, { state: stateFilter });
            setPrs(data.results || data);
        } catch { setPrs([]); }
        setLoading(false);
    }

    async function handleCreate(e) {
        e.preventDefault();
        try {
            await createPullRequest(repo.id, form);
            setCreating(false);
            setForm({ title: '', body: '', head_branch: '', base_branch: repo.default_branch || 'main', is_draft: false });
            loadPRs();
        } catch (err) { alert(err.message); }
    }

    async function handleMerge(prId) {
        if (!window.confirm('Смержить этот PR?')) return;
        try {
            await mergePR(repo.id, prId);
            setSelectedPR(null);
            loadPRs();
        } catch (err) { alert(err.message); }
    }

    async function handleClose(prId) {
        try {
            await closePR(repo.id, prId);
            setSelectedPR(null);
            loadPRs();
        } catch (err) { alert(err.message); }
    }

    if (selectedPR) {
        return (
            <PRDetail
                repoId={repo.id}
                pr={selectedPR}
                onBack={() => { setSelectedPR(null); loadPRs(); }}
                onMerge={handleMerge}
                onClose={handleClose}
                isOwner={isOwner}
            />
        );
    }

    return (
        <div className={styles.wrap}>
            <div className={styles.header}>
                <div className={styles.stateToggle}>
                    {['open', 'closed', 'merged'].map(s => (
                        <button key={s}
                            className={stateFilter === s ? styles.activeFilter : styles.filterBtn}
                            onClick={() => setStateFilter(s)}
                        >
                            {s === 'open' ? '● Открытые' : s === 'merged' ? '⟵ Смерженные' : '✗ Закрытые'}
                        </button>
                    ))}
                </div>
                {user && (
                    <button className={styles.newBtn} onClick={() => setCreating(true)}>
                        + Новый Pull Request
                    </button>
                )}
            </div>

            {creating && (
                <form className={styles.createForm} onSubmit={handleCreate}>
                    <h3>Создать Pull Request</h3>
                    <input
                        className={styles.inputField}
                        placeholder="Заголовок *"
                        value={form.title}
                        onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                        required
                    />
                    <textarea
                        className={styles.textareaField}
                        placeholder="Описание (необязательно)..."
                        value={form.body}
                        onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
                        rows={4}
                    />
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>Из ветки (head)</label>
                            <select className={styles.select} value={form.head_branch} onChange={e => setForm(p => ({ ...p, head_branch: e.target.value }))} required>
                                <option value="">Выберите ветку</option>
                                {branches.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                            </select>
                        </div>
                        <div className={styles.formGroup}>
                            <label>В ветку (base)</label>
                            <select className={styles.select} value={form.base_branch} onChange={e => setForm(p => ({ ...p, base_branch: e.target.value }))}>
                                {branches.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <label className={styles.checkboxLabel}>
                        <input type="checkbox" checked={form.is_draft} onChange={e => setForm(p => ({ ...p, is_draft: e.target.checked }))} />
                        Черновик PR
                    </label>
                    <div className={styles.formActions}>
                        <button type="button" className={styles.cancelBtn} onClick={() => setCreating(false)}>Отмена</button>
                        <button type="submit" className={styles.submitBtn}>Создать PR</button>
                    </div>
                </form>
            )}

            <div className={styles.list}>
                {loading ? (
                    <div className={styles.loading}>Загрузка...</div>
                ) : prs.length === 0 ? (
                    <div className={styles.empty}>
                        <span style={{ fontSize: 40 }}>🔀</span>
                        <p>Нет Pull Requests</p>
                    </div>
                ) : (
                    prs.map(pr => {
                        const stateColor = pr.state === 'open' ? '#4ade80' : pr.state === 'merged' ? '#a78bfa' : '#9ca3af';
                        const stateSymbol = pr.state === 'open' ? '●' : pr.state === 'merged' ? '⟵' : '✗';
                        return (
                            <div key={pr.id} className={styles.prRow} onClick={() => setSelectedPR(pr)}>
                                <div className={styles.prLeft}>
                                    <span className={styles.stateIcon} style={{ color: stateColor }}>{stateSymbol}</span>
                                    <div className={styles.prInfo}>
                                        <div className={styles.prTitleLine}>
                                            <span className={styles.prRowTitle}>{pr.title}</span>
                                            {pr.is_draft && <span className={styles.draftBadge}>Черновик</span>}
                                        </div>
                                        <div className={styles.prMeta2}>
                                            #{pr.number} &nbsp;
                                            <span className={styles.branchTag}>{pr.head_branch}</span>
                                            <span className={styles.branchArrow}>→</span>
                                            <span className={styles.branchTag}>{pr.base_branch}</span>
                                            &nbsp; создан {timeAgo(pr.created_at)} · {pr.author?.username}
                                        </div>
                                    </div>
                                </div>
                                {pr.state === 'merged' && (
                                    <div className={styles.prStats}>
                                        <span className={styles.additions}>+{pr.additions || 0}</span>
                                        <span className={styles.deletions}>-{pr.deletions || 0}</span>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
